import type { RefObject } from 'react';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import { findTransitionController } from '../../../../util/core-transition-component';
import { slideUp } from '../../../../util/animation/slide/slide';
import { revealRectangle } from '../../../../util/animation/reveal/reveal';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';
import type { VideoRef } from '../../../atoms/video/Video.data';
import gsap from 'gsap';

interface PeopleSliderItemRefs {
  elementRef: RefObject<HTMLElement | null>;
  imageRef?: RefObject<ImageRef | null>;
  labelRef?: RefObject<HeadingRef | null>;
  jobTitleRef?: RefObject<TextRef | null>;
  imageHolderRef?: RefObject<HTMLDivElement | null>;
  imageContainerRef?: RefObject<HTMLDivElement | null>;
  linkRef?: RefObject<PrimaryLinkRef | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  {
    imageRef,
    labelRef,
    jobTitleRef,
    imageHolderRef,
    imageContainerRef,
    linkRef,
  }: PeopleSliderItemRefs,
): void {
  const jobTitle = findTransitionController(jobTitleRef);
  const label = findTransitionController(labelRef);
  const link = findTransitionController(linkRef);

  if (imageHolderRef?.current) {
    timeline.add(
      slideUp(imageHolderRef.current, {
        from: 100,
        duration: 0.5,
      }),
      0,
    );
  }

  if (imageContainerRef?.current) {
    timeline.fromTo(
      imageContainerRef.current,
      {
        scale: 1.3,
      },
      {
        scale: 1,
        clearProps: 'scale',
        ease: 'Quad.easeInOut',
        duration: 0.5,
      },
      0,
    );
  }

  if (imageRef?.current) {
    timeline.add(
      revealRectangle(imageRef.current, {
        from: 'bottom',
        duration: 0.5,
      }),
      0,
    );
  }

  if (label) {
    timeline.add(label.getTimeline('in') as gsap.core.Timeline, 0.2);
  }

  if (jobTitle) {
    timeline.add(jobTitle.getTimeline('in') as gsap.core.Timeline, 0.4);
  }

  if (link) {
    timeline.add(link.getTimeline('in') as gsap.core.Timeline, 0.5);
  }
}

export function scaleImage(
  imageRef?: RefObject<HTMLDivElement | ImageRef | VideoRef | null>,
  zoom = true,
  scaleValue = 1.1,
): void {
  if (imageRef?.current) {
    let element = imageRef?.current;
    if ((imageRef?.current as VideoRef)?.elementRef?.current) {
      element = (imageRef.current as VideoRef).elementRef.current as HTMLDivElement;
    }

    gsap.to(element, {
      scale: zoom ? scaleValue : 1,
      ease: 'Quad.easeInOut',
      duration: 0.5,
    });
  }
}
